import { useState, useEffect, useCallback } from 'react'
import { authFetchJson } from '@/shared/lib/auth-fetch'

interface WorkScopePriority {
  workScope: string
  workScopeId: number
  workScopeDisplay: string
  priorities: {
    ready: Array<{ name: string; homeId: number; homeownerId?: number; homeownerUserId?: number; status: string; address?: string; statusDetail?: string | null; claimedBy?: string | null; isGoodwill?: boolean }>
    notReady: Array<{ name: string; homeId: number; homeownerId?: number; homeownerUserId?: number; status: string; address?: string; statusDetail?: string | null; claimedBy?: string | null; isGoodwill?: boolean }>
    /** Goodwill entries (same shape, at bottom of column); isGoodwill is true */
    goodwill?: Array<{ name: string; homeId: number; homeownerId?: number; homeownerUserId?: number; status: string; address?: string; statusDetail?: string | null; claimedBy?: string | null; isGoodwill: true }>
  }
  completed: number
  total: number
  totalNeeded: number
  percentage: number
}

export function usePriorityData(regionId?: number | null) {
  const [data, setData] = useState<WorkScopePriority[]>([])
  const [isLoading, setIsLoading] = useState(false) // Start as false, only set to true when actually fetching
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    if (!regionId) {
      console.log('[usePriorityData] No regionId provided, skipping fetch')
      setError('regionId is required')
      setIsLoading(false)
      return
    }

    console.log(`[usePriorityData] Fetching priority data for regionId: ${regionId}`)
    try {
      setIsLoading(true)
      setError(null)
      
      const res = await authFetchJson<any>(`/priority?regionId=${encodeURIComponent(regionId)}`)
      
      if (!res.success) {
        setError(`API Error: ${res.status || 'unknown'} - ${res.message || 'Unauthorized'}`)
        return
      }
      
      const result = res.data
      
      // Check if result is an array (direct data) or has success property
      if (Array.isArray(result)) {
        console.log(`[usePriorityData] Setting data (direct array, length: ${result.length})`)
        setData(result)
      } else if (res.success) {
        const dataToSet = result || []
        console.log(`[usePriorityData] Setting data (success object, length: ${Array.isArray(dataToSet) ? dataToSet.length : 'N/A'})`)
        setData(dataToSet)
      } else {
        setError('Failed to fetch priorities')
      }
    } catch (err) {
      console.error('[usePriorityData] Fetch error:', err)
      setError('Failed to fetch priorities')
    } finally {
      setIsLoading(false)
      console.log(`[usePriorityData] Finished loading`)
    }
  }, [regionId])

  const refreshData = async () => {
    if (!regionId) {
      setError('regionId is required')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      
      console.log(`Priority hook - Refreshing priorities via /priority/refresh for region ${regionId}`)
      
      // First, trigger the refresh endpoint to recalculate priorities
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
      
      const refreshRes = await authFetchJson<any>('/priority/refresh', {
        method: 'POST',
        body: JSON.stringify({ regionId }),
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (!refreshRes.success) {
        throw new Error(refreshRes.message || 'Failed to refresh priorities')
      }
      
      // Then fetch the updated data
      console.log(`Priority hook - Fetching updated data from /priority for region ${regionId}`)
      const fetchRes = await authFetchJson<any>(`/priority?regionId=${encodeURIComponent(regionId)}`)
      
      if (!fetchRes.success) {
        throw new Error(fetchRes.message || 'Failed to fetch updated priorities')
      }
      
      const fetchResult = fetchRes.data
      
      // Check if result is an array (direct data) or has success property
      if (Array.isArray(fetchResult)) {
        console.log('Priority hook - Setting updated data (direct array):', JSON.stringify(fetchResult, null, 2))
        setData(fetchResult)
      } else if (fetchRes.success) {
        const dataToSet = fetchResult || []
        console.log('Priority hook - Setting updated data (success object):', JSON.stringify(dataToSet, null, 2))
        setData(dataToSet)
      } else {
        setError('Failed to fetch updated priorities')
      }
    } catch (err) {
      console.error('Priority hook - Refresh error:', err)
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request timed out. The priority calculation is taking too long.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to refresh priorities')
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    console.log(`[usePriorityData] useEffect triggered, regionId: ${regionId}`)
    if (regionId) {
      console.log(`[usePriorityData] Calling fetchData for regionId: ${regionId}`)
      fetchData()
    } else {
      console.log(`[usePriorityData] No regionId, skipping fetch`)
    }
  }, [regionId, fetchData])

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    refresh: refreshData
  }
}
